import type { SymbolView } from "./SymbolView";

/** A 2D position in the grid root's local space (Pixi: y grows DOWN). */
export interface GridPoint {
  x: number;
  y: number;
}

/** Size of the layout area the grid is fitted into (`layoutRoot` UITransform). */
export interface GridLayoutArea {
  width: number;
  height: number;
}

export type GridLayoutConfig = {
  gridColumnCount: number;
  gridRowCount: number;

  cellWidth: number;
  cellHeight: number;

  /** Available area (the layout root's size). Null when no layout root is bound. */
  layoutArea: GridLayoutArea | null;
  autoFitToLayoutRoot: boolean;

  paddingLeft: number;
  paddingRight: number;
  paddingTop: number;
  paddingBottom: number;

  columnPaddingInner: number;
  columnPaddingMiddle: number;
  columnPaddingOuter: number;

  /**
   * Optional explicit per-column horizontal offsets (length = column count).
   * When provided, used verbatim and the columnPadding* fields above are
   * ignored; when absent, offsets fall back to the default 6-column even
   * layout [-po, -pm, -pi, +pi, +pm, +po]. Lets a mechanic supply a
   * different column count / layout (e.g. a 5-reel center-0 layout) without
   * altering the default path used by existing mechanics.
   */
  columnOffsets?: number[];

  centerGrid: boolean;
};

export type ComputedGridLayout = {
  cellWidth: number;
  cellHeight: number;
  originX: number;
  /** Row 0 center y. Rows grow DOWN (+y) from here. */
  originY: number;
  columnOffsets: number[];
};

/**
 * Pure grid geometry, y-down: y grows
 * downward, so row 0 sits at the TOP (`originY` is the smallest y) and each
 * following row is `+cellHeight` below it. Everything that consumed the earlier
 * `originY - row * cellHeight` reads `originY + row * cellHeight` here.
 */
export class GridLayout {
  public recalculate(cfg: GridLayoutConfig): ComputedGridLayout {
    let computedCellWidth = cfg.cellWidth;
    let computedCellHeight = cfg.cellHeight;

    let availableWidth = cfg.gridColumnCount * computedCellWidth;
    let availableHeight = cfg.gridRowCount * computedCellHeight;

    const layout = cfg.layoutArea;

    if (cfg.autoFitToLayoutRoot && layout) {
      const innerWidth = Math.max(1, layout.width - cfg.paddingLeft - cfg.paddingRight);
      const innerHeight = Math.max(1, layout.height - cfg.paddingTop - cfg.paddingBottom);

      computedCellWidth = innerWidth / cfg.gridColumnCount;
      computedCellHeight = innerHeight / cfg.gridRowCount;

      availableWidth = innerWidth;
      availableHeight = innerHeight;
    } else {
      availableWidth = cfg.gridColumnCount * computedCellWidth;
      availableHeight = cfg.gridRowCount * computedCellHeight;
    }

    let originX = 0;
    let originY = 0;

    if (cfg.centerGrid) {
      const padX = (cfg.paddingLeft - cfg.paddingRight) * 0.5;
      // padY = (bottom - top)/2 shifts the origin; in y-down space the
      // same authored padding shifts the top row DOWN by (top - bottom)/2.
      const padY = (cfg.paddingTop - cfg.paddingBottom) * 0.5;

      originX = -availableWidth * 0.5 + computedCellWidth * 0.5 + padX;
      originY = -availableHeight * 0.5 + computedCellHeight * 0.5 + padY;
    } else {
      originX = 0;
      originY = 0;
    }

    const pi = cfg.columnPaddingInner;
    const pm = cfg.columnPaddingMiddle;
    const po = cfg.columnPaddingOuter;
    // Opt-in explicit offsets (e.g. a 5-reel center-0 layout) take precedence;
    // otherwise the default 6-column even layout is used unchanged.
    const columnOffsets = cfg.columnOffsets ?? [-po, -pm, -pi, +pi, +pm, +po];

    return { cellWidth: computedCellWidth, cellHeight: computedCellHeight, originX, originY, columnOffsets };
  }

  public computeLocalPosition(layout: ComputedGridLayout, row: number, col: number): GridPoint {
    const offset = layout.columnOffsets[col] ?? 0;
    const x = layout.originX + col * layout.cellWidth + offset;
    const y = layout.originY + row * layout.cellHeight;
    return { x, y };
  }

  public rowColToIndex(cols: number, row: number, col: number): number {
    return row * cols + col;
  }

  public indexToRowCol(cols: number, index: number): { row: number; col: number } {
    return { row: Math.floor(index / cols), col: index % cols };
  }

  // ---------------------------------------------------------------------------
  // Deterministic accessors
  // ---------------------------------------------------------------------------

  public getIndexAt(input: { cols: number; row: number; col: number }): number {
    return input.row * input.cols + input.col;
  }

  public getSymbolAt(input: {
    symbolsByIndex: Map<number, SymbolView>;
    cols: number;
    row: number;
    col: number;
    requireActive?: boolean;
  }): SymbolView | null {
    const index = this.rowColToIndex(input.cols, input.row, input.col);
    const v = input.symbolsByIndex.get(index) ?? null;
    if (!v) return null;
    if (input.requireActive && !v.node.visible) return null;
    return v;
  }

  public getColumnIndices(input: {
    cols: number;
    rows: number;
    col: number;
    order?: "topToBottom" | "bottomToTop";
  }): number[] {
    const order = input.order ?? "topToBottom";
    const out: number[] = [];

    if (order === "topToBottom") {
      for (let row = 0; row < input.rows; row++) {
        out.push(this.rowColToIndex(input.cols, row, input.col));
      }
    } else {
      for (let row = input.rows - 1; row >= 0; row--) {
        out.push(this.rowColToIndex(input.cols, row, input.col));
      }
    }

    return out;
  }

  public getColumnSymbols(input: {
    symbolsByIndex: Map<number, SymbolView>;
    cols: number;
    rows: number;
    col: number;
    order?: "topToBottom" | "bottomToTop";
    requireActive?: boolean;
  }): SymbolView[] {
    const indices = this.getColumnIndices({
      cols: input.cols,
      rows: input.rows,
      col: input.col,
      order: input.order,
    });

    const out: SymbolView[] = [];
    for (const idx of indices) {
      const v = input.symbolsByIndex.get(idx);
      if (!v) continue;
      if (input.requireActive && !v.node.visible) continue;
      out.push(v);
    }
    return out;
  }
}
